/**
 * Timed Release Product Page Handler
 * 
 * Handles showing/hiding the add-to-cart button on product pages
 * based on timed release status.
 */
(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const productPage = document.querySelector('.product-single');
    if (!productPage) {
      return; // Not on a product page
    }

    // Check if this is a timed release product
    const timedReleaseControls = document.querySelector('[data-timed-release-controls]');
    if (!timedReleaseControls) {
      return; // Not a timed release product
    }

    // Find the add-to-cart button wrapper
    const addToCartWrapper = timedReleaseControls.querySelector('.product__add');
    const addToCartButton = timedReleaseControls.querySelector('input[type="submit"][name="add"]');
    
    if (!addToCartWrapper || !addToCartButton) {
      console.warn('TimedReleaseProduct: Add to cart button not found');
      return;
    }

    // Store what the button should say when enabled (before we change it to "Available Soon")
    // This helps us restore the correct state later
    if (!addToCartButton.getAttribute('data-original-value')) {
      const currentValue = addToCartButton.value;
      // If button already says "Available Soon" from server, we need to determine what it should be
      if (currentValue === 'Available Soon') {
        // Determine correct value based on product state
        const form = addToCartButton.closest('form');
        const variantInput = form ? form.querySelector('input[name="id"]') : null;
        const variantId = variantInput ? variantInput.value : null;
        
        // Store what the button should be when enabled
        if (variantId && variantId !== '') {
          addToCartButton.setAttribute('data-original-value', 'Add to Bag');
        } else {
          addToCartButton.setAttribute('data-original-value', 'Select A Size');
        }
      } else if (currentValue !== 'Select A Size' && currentValue !== 'Sold Out') {
        // Store the current value as the original (unless it's a placeholder)
        addToCartButton.setAttribute('data-original-value', currentValue);
      }
    }

    // Check if timer has already expired (page loaded after release time)
    const timer = document.querySelector('timed-release-timer');
    if (timer) {
      const status = timer.getAttribute('status');
      if (status === 'expired') {
        // Already expired, enable button immediately
        showAddToCartButton(addToCartWrapper, addToCartButton);
      } else {
        // Still counting down, set button to "Available Soon" (disabled)
        setButtonToAvailableSoon(addToCartWrapper, addToCartButton);
      }
    } else {
      // No timer found - this could mean:
      // 1. Timer component hasn't loaded yet (race condition)
      // 2. Release time has already passed (timer not rendered)
      // 3. This isn't actually a timed release product
      
      // Use MutationObserver to watch for timer element appearing
      const observer = new MutationObserver((mutations, obs) => {
        const delayedTimer = document.querySelector('timed-release-timer');
        if (delayedTimer) {
          obs.disconnect(); // Stop observing once timer is found
          const status = delayedTimer.getAttribute('status');
          if (status === 'expired') {
            showAddToCartButton(addToCartWrapper, addToCartButton);
          } else {
            setButtonToAvailableSoon(addToCartWrapper, addToCartButton);
          }
        }
      });
      
      // Observe the document body for new elements
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Also check after a short delay in case MutationObserver misses it
      // or timer loads before observer is set up
      setTimeout(() => {
        const delayedTimer = document.querySelector('timed-release-timer');
        if (delayedTimer) {
          observer.disconnect();
          const status = delayedTimer.getAttribute('status');
          if (status === 'expired') {
            showAddToCartButton(addToCartWrapper, addToCartButton);
          } else {
            setButtonToAvailableSoon(addToCartWrapper, addToCartButton);
          }
        } else {
          // Timer still not found after delay - check button state
          observer.disconnect();
          // If button already says "Available Soon", keep it disabled
          // Otherwise, assume release time has passed and enable button
          if (addToCartButton.value === 'Available Soon') {
            setButtonToAvailableSoon(addToCartWrapper, addToCartButton);
          } else {
            // Button doesn't say "Available Soon", so likely not a timed release
            // or release time has passed - enable button
            showAddToCartButton(addToCartWrapper, addToCartButton);
          }
        }
      }, 200);
    }

    // Get the product ID from the timer element on this page
    const pageTimer = document.querySelector('timed-release-timer');
    const pageProductId = pageTimer ? String(pageTimer.getAttribute('product-id')) : null;

    // Function to handle timer expiration
    function handleTimerExpiration(eventProductId) {
      // Enable button if the event is for this product's timer
      if (pageProductId && eventProductId && String(eventProductId) === pageProductId) {
        showAddToCartButton(addToCartWrapper, addToCartButton);
        return true;
      } else if (pageTimer && (!eventProductId || String(eventProductId) === pageProductId)) {
        // Fallback: if we have a timer on the page, enable it
        showAddToCartButton(addToCartWrapper, addToCartButton);
        return true;
      }
      return false;
    }

    // Listen for timer expiration event (bubbles from timer element)
    document.addEventListener('timed-release:expired', function(event) {
      const eventProductId = event.detail?.productId;
      handleTimerExpiration(eventProductId);
    });

    // Also listen directly on the timer element in case event doesn't bubble
    if (pageTimer) {
      pageTimer.addEventListener('timed-release:expired', function(event) {
        const eventProductId = event.detail?.productId;
        handleTimerExpiration(eventProductId);
      });
    }
  }

  function showAddToCartButton(wrapper, button) {
    if (!wrapper || !button) return;

    console.log('TimedReleaseProduct: Enabling button, current value:', button.value);

    // Show the wrapper (in case it was hidden)
    wrapper.style.display = '';
    wrapper.classList.remove('timed-release--hidden');

    // Enable the button
    button.disabled = false;
    button.classList.remove('timed-release--disabled');

    // Restore original button text/value
    const originalValue = button.getAttribute('data-original-value');
    if (originalValue) {
      button.value = originalValue;
      button.removeAttribute('data-original-value');
      console.log('TimedReleaseProduct: Restored original value:', originalValue);
    } else if (button.value === 'Available Soon') {
      // If no original value stored and button says "Available Soon", 
      // determine the correct value based on product state
      // Check if product has variants and if a variant is selected
      const form = button.closest('form');
      const variantInput = form ? form.querySelector('input[name="id"]') : null;
      const variantId = variantInput ? variantInput.value : null;
      
      // Default to 'Add to Bag' if variant is selected, otherwise 'Select A Size'
      if (variantId && variantId !== '') {
        button.value = 'Add to Bag';
        // Update button class to match enabled state
        button.className = button.className.replace(/btn--default/g, 'btn--black btn--wide');
      } else {
        button.value = 'Select A Size';
      }
      console.log('TimedReleaseProduct: Set default value:', button.value);
    }

    // Trigger any necessary events for other scripts
    const event = new CustomEvent('timed-release:button-enabled', {
      bubbles: true,
      cancelable: true
    });
    wrapper.dispatchEvent(event);
  }

  function setButtonToAvailableSoon(wrapper, button) {
    if (!wrapper || !button) return;

    // Show the wrapper (button should be visible)
    wrapper.style.display = '';
    wrapper.classList.remove('timed-release--hidden');

    // Store original button value if not already stored
    if (!button.getAttribute('data-original-value')) {
      const currentValue = button.value;
      // Only store if it's not already "Available Soon"
      if (currentValue !== 'Available Soon' && currentValue !== 'Select A Size' && currentValue !== 'Sold Out') {
        button.setAttribute('data-original-value', currentValue);
      }
    }

    // Disable the button
    button.disabled = true;
    button.classList.add('timed-release--disabled');

    // Update button text to "Available Soon"
    button.value = 'Available Soon';
  }
})();

